import React, {useEffect, useCallback, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import PropTypes from "prop-types";
import {useLocation} from "react-router-dom";
import {useInView} from "react-intersection-observer";
import {useScreenshot, createFileName} from "use-react-screenshot";
import {CellIdContext} from "../../../../contexts/cellIdContext";
import {
  requestDashboardCellsData,
  deleteDashboardCell,
} from "../../../../redux/Actions/tsdb.actions";
import {getQueryDataAsync} from "../../../../redux/AsyncActions/tsdb.async";
import {getDashboardCellsData} from "../../../../redux/Selectors/tsdb.selector";
import DeleteModal from "../../../Modal/DeleteModal";
import CellContext from "./CellContext";
import CellHeader from "./CellHeader";
import View from "./View/View";
import "./cell.scss";

const Cell = (props) => {
  const {
    cell = {},
    timeZone = "UTC",
    timeRangeStart,
    timeRangeStop,
    windowPeriod,
    refreshCount = 0,
  } = props;
  const {id, name, properties} = cell;
  const {queries = []} = properties;
  const location = useLocation();
  const {ref: inViewRef, inView} = useInView({
    threshold: 0.5,
  });
  const [chartData, setChartData] = useState("");
  const [loading, setLoading] = useState(false);
  const [isDataLoaded, setDataLoaded] = useState(true);
  const [modalOpen, toggleModal] = useState(false);
  const dispatch = useDispatch();
  const currentDashboard = useSelector(getDashboardCellsData);

  /** This component make API call for cell query and passes response data to cell view component */

  const getData = useCallback(
    async (format = "", withLoadingState = true) => {
      // format csv or json
      const query = queries[0];

      if (!query) {
        if (format === "csv") {
          return "";
        } else {
          return [];
        }
      }
      if (withLoadingState) {
        setLoading(true);
      }

      try {
        const payload = {
          output_format: format,
          query: query.text,
        };

        if (timeRangeStart) {
          payload.timeRangeStart = timeRangeStart;
        }

        if (timeRangeStop) {
          payload.timeRangeStop = timeRangeStop;
        }
        if (windowPeriod) {
          payload.windowPeriod = windowPeriod;
        }

        const response = await getQueryDataAsync(payload);
        return response.data;
      } catch (e) {
        return format === "csv" ? "" : [];
      } finally {
        setLoading(false);
      }
    },
    [queries, timeRangeStart, timeRangeStop, windowPeriod]
  );

  const getChartData = useCallback(async () => {
    const data = await getData("json");
    setChartData(data);
    setDataLoaded(true);
  }, [getData]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDataLoaded(false);
  }, [timeRangeStart, timeRangeStop, refreshCount, cell]);

  useEffect(() => {
    if (inView && !isDataLoaded) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      getChartData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, isDataLoaded]);

  const onDelete = () => {
    toggleModal(true);
  };

  const updateDashboard = () => {
    dispatch(requestDashboardCellsData(currentDashboard.id));
  };

  const isAlertsPage = location.pathname.split("/")[2] === "alerts";

  const [, takeScreenShot] = useScreenshot({
    type: "image/png",
    quality: 1.0,
  });

  const downloadScreenshot = () => {
    takeScreenShot(document.getElementById(`cell-container-${id}`)).then((image) => {
      const a = document.createElement("a");
      a.href = image;
      a.download = createFileName("png", name);
      a.click();
    });
  };

  const handleDelete = () => {
    const callback = () => {
      toggleModal(false);
      updateDashboard();
    };
    dispatch(deleteDashboardCell(id, callback));
  };

  const isEditable = location.state && location.state.isEditable;

  return (
    <div
      id={`cell-container-${id}`}
      style={{minHeight: "100%", display: "flex", flexDirection: "column"}}
    >
      {modalOpen && (
        <DeleteModal
          header={`Delete ${name}`}
          modalBody={"Are you sure you want to delete this Dashboard cell ?"}
          onClose={() => toggleModal(false)}
          onDelete={handleDelete}
          open={modalOpen}
        />
      )}
      <CellHeader name={name}>
        <CellContext
          downloadScreenshot={downloadScreenshot}
          filename={name}
          getData={getData}
          isHidden={isAlertsPage}
          onDelete={onDelete}
          onRefresh={getChartData}
          showDelete={isEditable}
        />
      </CellHeader>
      <CellIdContext.Provider value={id}>
        <div className="cell--view" id={`cell-${id}`} ref={inViewRef}>
          {properties && (
            <View
              cellId={id}
              chartData={chartData}
              properties={properties}
              loading={loading}
              timeZone={timeZone}
              windowPeriod={windowPeriod}
            />
          )}
        </div>
      </CellIdContext.Provider>
    </div>
  );
};

Cell.propTypes = {
  cell: PropTypes.object.isRequired,
  timeZone: PropTypes.string,
  refreshCount: PropTypes.number,
  timeRangeStart: PropTypes.string,
  timeRangeStop: PropTypes.string,
};

export default Cell;
